import { inject, Injectable } from '@angular/core';
import {
  GtmBaseData,
  GtmClickLinkEventData,
  GtmDownloadEventData,
  GtmEventCategory,
  GtmEvent,
  GtmEventId,
  GtmLoginEventData,
  GtmPageViewEventData,
  GtmSearchEventData,
  GtmUserProperties,
  UserStatus,
} from '../types/gtm';
import { environment } from '../../../environments/environment';
import { UserSessionData } from '../types/session-data';
import { LOCAL_STORAGE_TOKEN } from '../constants/constants';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  private digitalData: GtmBaseData | null = null;
  private readonly country = 'es';
  private readonly brand = 'mapfre';
  private readonly asset = 'myglobalrisks';
  private prev_screen_name = '';
  screen_name = '';
  private userStatus: UserStatus = 'no_logged';
  private lang = '';
  private userProperties: GtmUserProperties = {
    client_user_id_up: '',
    client_id_ric_up: '',
    client_id_up: '',
    client_type_up: '',
    client_plans_up: '',
  };

  private readonly _translateService = inject(TranslateService);

  private get dataLayer(): object[] {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  private getScreenName(screen_name: string): string {
    if (screen_name) {
      this.prev_screen_name = this.screen_name
        ? `${this.country}:${this.brand}:${this.asset}:${this.screen_name}`
        : '';
      this.screen_name = `${screen_name}`;
    } else {
      this.prev_screen_name = `${this.country}:${this.brand}:${this.asset}:${this.screen_name}`;
    }
    return `${this.country}:${this.brand}:${this.asset}:${this.screen_name}`;
  }

  eventLabel(label: string): string {
    return this._translateService.instant(label);
  }

  sessionId(): string {
    const idToken = localStorage.getItem(LOCAL_STORAGE_TOKEN);
    let session_id = '';

    // analitycs session_id generation
    const parts = idToken?.split('.') || [];
    session_id =
      parts.length === 3
        ? parts[2].substring(0, 20)
        : idToken?.substring(0, 20) || '';
    return session_id;
  }

  setUserStatus(userStatus: UserStatus): void {
    this.userStatus = userStatus;
  }

  setUserLang(lang: string): void {
    this.lang = lang;
  }

  publishUserProperties(): void {
    this.dataLayer.push({ ...this.userProperties });
  }

  initDataLayer(): void {
    this.digitalData = {
      ...this.digitalData,
      application: 'web',
      environment: environment.name,
      brand: this.brand,
      asset: this.asset,
      user_status: this.userStatus,
      country: this.country,
    };
  }

  loadUserProperties(userData: UserSessionData | null): void {
    const isLogged = !!userData;
    this.setUserStatus(isLogged ? 'logged' : 'no_logged');

    if (this.digitalData) {
      const roles = userData?.roles ?? [];
      this.digitalData = {
        ...this.digitalData,
        user_status: this.userStatus,
        user_rol: roles[0] ?? '',
        user_type: isLogged
          ? roles.includes('external')
            ? 'external'
            : 'internal'
          : '',
      };
    }
  }

  private push(event: GtmEvent, category: GtmEventCategory): void {
    const eventData = {
      ...this.digitalData,
      ...event,
      event_category: category,
      event_category_ep: category,
      language: this.lang,
      user_status: this.userStatus,
      prev_screen_name: this.prev_screen_name,
      session_id: this.sessionId(),
      timestamp: Date.now(),
    };
    this.dataLayer.push(eventData);
  }

  pushEventPageView(screenName: string): void {
    const currentScreenName = this.getScreenName(screenName);
    const pageView: GtmPageViewEventData = {
      event: 'page_view',
      asset_ep: this.asset,
      asset_version_ep: '',
      brand_ep: this.brand,
      country_ep: this.country,
      department_ep: '',
      enviroment_ep: environment.name,
      language_ep: this.lang,
      page_type_ep: '',
      previous_page_ep: this.prev_screen_name,
      screen_name_ep: currentScreenName,
      section_1_ep: '',
      section_2_ep: '',
      section_3_ep: '',
      section_4_ep: '',
      section_5_ep: '',
      timestamp_ep: Date.now(),
      user_status_ep: this.userStatus,
      product_id_ep: '',
      product_name_ep: '',
      url_ep: window.location.href,
      referrer_ep: document.referrer,
      front_end_technology_used_ep: '',
      campaign_id_ep: '',
      gclid_ep: '',
      campaign_origin_ep: '',
      campaign_pid1_ep: '',
      source_ep: '',
      medium_ep: '',
      campaign_ep: '',
      term_ep: '',
      content_ep: '',
      marketing_tactic_ep: '',
      creative_format_ep: '',
    };
    this.dataLayer.push(pageView);
  }

  pushEventModalView(screenName: string): void {
    this.pushEventPageView(`modal_${screenName}`);
  }

  pushEventLogin(
    screenName: string,
    result: 'success' | 'error',
    errorType?: string
  ): void {
    const login: GtmLoginEventData = {
      event: 'login',
      event_label_ep:
        result === 'success'
          ? 'success'
          : `error - ${errorType?.trim() || 'unknown'}`,
    };
    this.dataLayer.push(login);
  }

  pushEventDownload(elementId: string, elementText: string): void {
    const download: GtmDownloadEventData = {
      event: 'download',
      event_category: 'download',
      event_category_ep: 'download',
      event_detail_ep: elementId,
      event_label_ep: this.eventLabel(elementText),
    };
    this.dataLayer.push(download);
  }

  pushEventSubmit(
    screenName: string,
    eventId: GtmEventId,
    description: string | undefined = undefined,
    category: GtmEventCategory = 'content actions'
  ): void {
    this.push(
      {
        event: 'submit_event',
        event_id: eventId,
        screen_name: this.getScreenName(screenName),
        description: `Call to ${eventId} service correctly. ${description}`,
      },
      category
    );
  }

  pushEventSubmitError(
    screenName: string,
    eventId: GtmEventId,
    error: string,
    description?: string
  ): void {
    this.push(
      {
        event: 'submit_event_error',
        event_id: eventId,
        error_type: error,
        screen_name: this.getScreenName(screenName),
        description: `Call to ${eventId} service failed. ${description}`,
      },
      'error'
    );
  }

  pushEventClickButton(
    screenName: string,
    elementId: string,
    elementText: string,
    category: GtmEventCategory = 'body button'
  ): void {
    const eventLabel = this.eventLabel(elementText);
    this.push(
      {
        event: 'click_button',
        screen_name: this.getScreenName(screenName),
        element_id: `${this.screen_name}-btn-${elementId}`,
        element_label: eventLabel,
        description: `User clicked on ${eventLabel} button.`,
      },
      category
    );
  }

  pushEventClickLink(
    screenName: string,
    elementId: string,
    elementText: string,
    category: GtmEventCategory = 'body links'
  ): void {
    const eventLabel = this.eventLabel(elementText);
    const clickLink: GtmClickLinkEventData = {
      event: 'click_link',
      event_category: category,
      event_category_ep: category,
      event_detail_ep: elementId,
      event_label_ep: eventLabel,
    };
    this.dataLayer.push(clickLink);
  }

  pushEventRadioButton(
    screenName: string,
    elementId: string,
    elementText: string,
    category: GtmEventCategory = 'content actions'
  ): void {
    const eventLabel = this.eventLabel(elementText);
    this.push(
      {
        event: 'click_radio_button',
        screen_name: this.getScreenName(screenName),
        element_id: `${this.screen_name}-radio-${elementId}`,
        element_label: eventLabel,
        description: `User selected ${eventLabel} radio button.`,
      },
      category
    );
  }
  pushEventUpdate(
    screenName: string,
    elementId: string,
    elementText: string,
    category: GtmEventCategory = 'content actions'
  ): void {
    this.push(
      {
        event: 'event_update',
        screen_name: this.getScreenName(screenName),
        element_id: `${this.screen_name}-${elementId}`,
        description: `User ${elementText} data.`,
      },
      category
    );
  }

  pushEventSearch(
    screenName: string,
    elementId: string,
    elementText: string
  ): void {
    const search: GtmSearchEventData = {
      event: 'search',
      event_category: 'search performed',
      event_category_ep: 'search performed',
      event_detail_ep: elementId,
      event_label_ep: elementText,
      searck_keyword_ep: elementText,
    };
    this.dataLayer.push(search);
  }
}
